import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import BankAccountStatus from '@/enums/bankAccountStatus';

import BankAccountOnFileWithPlaidVerifyContainer from '../Plaid/BankAccountOnFileWithPlaidVerifyContainer';

type Props = {
    bankAccounts: BankAccount[];
    deleteBankAccount?: (bankAccountId: number) => void;
    selectBankAccount: (bankAccount: BankAccount) => void;
};

const PaymentMethodSelection = ({ bankAccounts, deleteBankAccount, selectBankAccount }: Props) => {
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
    const previousNumberOfBankAccounts = usePrevious(bankAccounts?.length);

    useEffect(() => {
        if (
            Boolean(bankAccounts) &&
            (!selectedPaymentMethodId || previousNumberOfBankAccounts !== bankAccounts?.length)
        ) {
            const defaultBankAccount = bankAccounts.find((bankAccount) => bankAccount.defaultAccount);
            setSelectedPaymentMethodId(`bankAccount-${defaultBankAccount?.bankAccountId}`);
            //just to make sure the parent is on the same page.
            if (defaultBankAccount && selectBankAccount) {
                selectBankAccount(defaultBankAccount);
            }
        }
    }, [bankAccounts, previousNumberOfBankAccounts, selectBankAccount, selectedPaymentMethodId]);

    const bankAccountRadios = useMemo(() => {
        if (!bankAccounts) {
            return null;
        }
        return bankAccounts.map((bankAccount) => {
            const radioId = `bankAccount-${bankAccount.bankAccountId}`;
            const selected = selectedPaymentMethodId === radioId;
            const disabled =
                bankAccount.status === BankAccountStatus.Pending ||
                bankAccount.status === BankAccountStatus.PendingManual ||
                bankAccount.status === BankAccountStatus.PendingAutomatic;
            return (
                <StyledRadio
                    disabled={disabled}
                    id={radioId}
                    key={bankAccount.bankAccountId}
                    label={
                        <div>
                            <BankAccountOnFileWithPlaidVerifyContainer
                                bankAccount={bankAccount}
                                deleteAccount={
                                    deleteBankAccount
                                        ? (event) => {
                                              event.stopPropagation();
                                              if (selected) {
                                                  setSelectedPaymentMethodId(undefined);
                                              }
                                              deleteBankAccount(bankAccount.bankAccountId);
                                          }
                                        : undefined
                                }
                                deleteAccountLinkText="Remove"
                            />
                        </div>
                    }
                    onClick={(event) => {
                        const id = event.currentTarget.id;
                        setSelectedPaymentMethodId(id);
                        selectBankAccount(bankAccount);
                    }}
                    selectedName={selectedPaymentMethodId}
                />
            );
        });
    }, [bankAccounts, deleteBankAccount, selectBankAccount, selectedPaymentMethodId]);

    return (
        <div>
            {/* Not using Radios component here so we can fully control it ourselves. */}
            {bankAccountRadios}
        </div>
    );
};

export default PaymentMethodSelection;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))`
    width: 100%;
    margin-bottom: 24px;

    .radioCircleClassName {
        top: 2px;
        left: 0;
    }
`;
